/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback } from 'react';

import useCache from '../useCache/index.ts';

/**
 * An identity maintained reference to the most recently passed in callback function
 * Can access this function by accessing the `current` property
 */
export type CachedCallback<T> = T & { current: T };

/**
 * Caches a callback function while guaranteeing stable identity for React.
 *
 * @param {function} callback The function to cache
 * @returns {function} A stable reference to the same function passed in.
 *
 * @example <caption>Example Usage</caption>
 * const fn = useEventCallback(() => { console.log('action'); })
 */
export default function useEventCallback<T extends (...args: any[]) => any>(
  callback: T
): CachedCallback<(...args: Parameters<T>) => ReturnType<T>> {
  const cachedCallback = useCache(callback);

  return Object.assign(
    useCallback(((...args) => cachedCallback.current(...args)) as T, [cachedCallback]),
    { current: callback }
  );
}
